import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Menu, ChevronDown, Eye, Search, CalendarDays, Bell, X, CheckCheck, LogOut, Settings,
} from 'lucide-react'
import api from '../api/client'
import { useAuth } from '../auth'
import ThemeToggle from '../theme/ThemeToggle'
import AssinaturaCdxCard from './AssinaturaCdxCard'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }
const roleLabel = (p) => (p === 'admin' ? 'Administrador' : p === 'licenciado' ? 'Licenciado' : 'Cliente')

/* ===== Menu do usuário (padrão Sol Magazine) — avatar + dropdown com dados, tema e sair ===== */
function UserMenu() {
  const { user, logout } = useAuth()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const fn = (e) => { ref.current && !ref.current.contains(e.target) && setOpen(false) }
    document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn)
  }, [])
  const inicial = (user?.nome || 'U').charAt(0).toUpperCase()
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} title="Minha conta"
        className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-xl border border-accent-300 hover:border-accent-500 transition-colors tc-hcard">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{inicial}</div>
        <ChevronDown size={15} className="text-accent-600 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 tc-pop">
          <div className="p-4 bg-gradient-to-r from-primary-50 to-accent-50 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center text-white font-bold flex-shrink-0">{inicial}</div>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-graphite truncate">{user?.nome}</p>
                <p className="text-[11px] text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-bold uppercase tracking-wide">{roleLabel(user?.perfil)}</span>
          </div>
          <button onClick={() => { setOpen(false); nav('/minha-conta') }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 flex items-center gap-2.5 transition-colors">
            <Settings size={15} className="text-gray-400" /> Minha conta
          </button>
          <div className="px-4 py-2.5 border-t border-gray-100">
            <p className="text-[10px] uppercase font-bold text-gray-400 mb-1.5">Tema</p>
            <ThemeToggle />
          </div>
          <button onClick={logout}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2.5 border-t border-gray-100 transition-colors">
            <LogOut size={15} /> Sair da plataforma
          </button>
        </div>
      )}
    </div>
  )
}

/* ===== Sino de notificações (D6) ===== */
function Notifications() {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [unread, setUnread] = useState(0)
  const ref = useRef(null)

  const reload = () => {
    api.get('/notificacoes/count-unread').then((r) => setUnread(r.data?.count ?? r.data?.total ?? 0)).catch(() => {})
    api.get('/notificacoes?page=1&limit=8').then((r) => setItems(r.data?.data || r.data || [])).catch(() => {})
  }
  useEffect(() => { reload(); const t = setInterval(reload, 60000); return () => clearInterval(t) }, [])
  useEffect(() => {
    const fn = (e) => { ref.current && !ref.current.contains(e.target) && setOpen(false) }
    document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn)
  }, [])

  const marcarTodas = () => api.put('/notificacoes/marcar-todas-lidas').then(reload).catch(() => {})
  const abrir = (e) => {
    if (!e.lida) api.put(`/notificacoes/${e.id}/marcar-lida`).then(reload).catch(() => {})
    if (e.link) { setOpen(false); nav(String(e.link).replace(/^#/, '')) }
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-xl hover:bg-primary-50 text-primary-700 transition-colors">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent-500 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-accent-600" />
              <h3 className="font-heading font-extrabold text-graphite text-sm">Notificações</h3>
              {unread > 0 && <span className="px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-bold">{unread}</span>}
            </div>
            {unread > 0 && (
              <button onClick={marcarTodas} className="text-[11px] text-accent-600 hover:text-accent-700 font-semibold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white/60">
                <CheckCheck size={12} /> Marcar todas
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-y-auto tc-scroll">
            {items.length === 0 ? (
              <div className="py-10 text-center text-neutral-400 text-xs">Nenhuma notificação</div>
            ) : items.map((e) => (
              <div key={e.id} onClick={() => abrir(e)} className={`px-4 py-3 border-b border-neutral-50 flex items-start gap-3 cursor-pointer hover:bg-primary-50/60 ${e.lida ? '' : 'bg-primary-50/30'}`}>
                <span className={`w-1.5 h-1.5 mt-1.5 rounded-full flex-shrink-0 ${e.lida ? '' : 'bg-accent-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${e.lida ? 'font-medium text-neutral-700' : 'font-bold text-graphite'}`}>{e.titulo}</p>
                  {e.mensagem && <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{e.mensagem}</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50">
            <button onClick={() => { setOpen(false); nav('/registros/fila-analise') }} className="w-full text-center text-xs font-semibold text-accent-600 hover:text-accent-700 py-1">Ver Fila de Análise</button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ===== Seletor "Ver como" (Simulador) — adaptado ao token-swap (verComo) ===== */
function Simulador() {
  const { verComo, sairSimulacao, impersonating, user } = useAuth()
  const [open, setOpen] = useState(false)
  const [list, setList] = useState([])
  const [q, setQ] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    const realToken = load('real')?.token || localStorage.getItem('token')
    api.get('/licenciados?limit=100', { headers: { Authorization: `Bearer ${realToken}` } })
      .then((r) => setList(r.data?.data || [])).catch(() => {})
  }, [])
  useEffect(() => {
    const fn = (e) => { ref.current && !ref.current.contains(e.target) && (setOpen(false), setQ('')) }
    document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn)
  }, [])

  const filtered = useMemo(() => {
    if (!q) return list
    const t = q.toLowerCase()
    return list.filter((l) => (l.nome || '').toLowerCase().includes(t) || (l.codigo_indicacao || '').toLowerCase().includes(t) || (l.email || '').toLowerCase().includes(t))
  }, [list, q])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); setQ('') }}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${impersonating ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-800' : 'bg-gradient-to-r from-primary-50 to-accent-50 border-accent-400 text-primary-700 hover:border-accent-500'}`}
      >
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${impersonating ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}>
          {impersonating ? (user?.nome || '?').charAt(0) : <Eye size={13} />}
        </div>
        <div className="text-left min-w-0 hidden md:block">
          <p className="text-xs font-medium leading-tight truncate max-w-[120px]">{impersonating ? user?.nome : 'Selecionar Licenciado'}</p>
          <p className="text-[10px] uppercase font-semibold opacity-70">{impersonating ? 'Simulando' : 'Simulador'}</p>
        </div>
        <ChevronDown size={14} className="ml-1 opacity-50 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar licenciado..." autoFocus
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-primary-400" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto tc-scroll">
            <button onClick={() => { sairSimulacao(); setOpen(false) }}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors flex items-center gap-2 ${impersonating ? 'text-gray-700' : 'bg-primary-50 text-primary-700 font-medium'}`}>
              <Eye size={14} className="text-gray-400" /> Visão Geral (Admin)
            </button>
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-400 text-center">Nenhum licenciado encontrado</div>
            ) : filtered.map((l) => (
              <button key={l.id} onClick={() => { verComo('licenciado', l.id); setOpen(false) }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors text-gray-700">
                <span className="font-medium">{l.nome}</span>
                <span className="text-gray-400 text-xs ml-2">- {l.codigo_indicacao}</span>
              </button>
            ))}
          </div>
          {impersonating && (
            <div className="p-2 border-t border-gray-100 bg-amber-50">
              <button onClick={() => { sairSimulacao(); setOpen(false) }} className="w-full flex items-center justify-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900 py-1">
                <X size={12} /> Sair da simulação
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function Header({ onMenuToggle }) {
  const { user, isAdminReal, logout } = useAuth()
  const nav = useNavigate()
  const [now, setNow] = useState(new Date())
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t) }, [])

  const data = now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const hora = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const inicial = (user?.nome || 'U').charAt(0)

  return (
    <header className="tc-header h-14 sm:h-16 bg-white/80 backdrop-blur-md border-b border-primary-100 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <button onClick={onMenuToggle} className="lg:hidden p-2 rounded-lg hover:bg-primary-50 text-primary-700 transition-colors flex-shrink-0">
          <Menu size={22} />
        </button>
        {/* Card usuário — clicável: abre Minha Conta (editar dados de acesso) */}
        <button onClick={() => nav('/minha-conta')} title="Minha conta"
          className="group flex items-center gap-2 px-3 py-2 rounded-xl hover:border-accent-500 transition-colors tc-hcard">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{inicial}</div>
          <div className="min-w-0 text-left hidden sm:block">
            <p className="text-sm font-bold text-graphite truncate max-w-[140px] leading-tight">{user?.nome}</p>
            <p className="text-[10px] text-accent-700 uppercase font-bold leading-tight">{roleLabel(user?.perfil)}</p>
          </div>
          <Settings size={14} className="text-accent-600 opacity-70 group-hover:opacity-100 hidden sm:block" />
        </button>
        {/* Seletor Ver como (admin) */}
        {isAdminReal && <Simulador />}
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        {/* Seletor de tema (Claro/Escuro/Auto) — visível no desktop; também no menu do usuário */}
        <div className="hidden lg:block"><ThemeToggle showLabels={false} /></div>
        <Notifications />
        {/* Assinatura CDX: mensalidade do Serviço Cloud VIP. Só o admin real enxerga
            (o próprio componente também checa isAdminReal, e a rota exige admin). */}
        <AssinaturaCdxCard />
        {/* Card data/hora */}
        <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl tc-hcard">
          <CalendarDays size={14} className="text-accent-600" />
          <div className="text-right">
            <p className="text-xs font-bold text-graphite leading-tight">{data}</p>
            <p className="text-[10px] font-mono font-bold text-accent-700 leading-tight">{hora}</p>
          </div>
        </div>
        {/* Menu do usuário (dados + tema + sair) — padrão Sol Magazine */}
        <UserMenu />
      </div>
    </header>
  )
}
